import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api/client';
import { useIsSiteAdmin } from '../../lib/api/hooks';

interface PoItem { sku: string; description: string; costCents: number }

interface StoreProduct {
  productId: string;
  name: string;
  description?: string;
  stripePriceId: string;
  stripeProductId?: string;
  priceCents: number;
  imageUrl?: string;
  imageKey?: string;
  category?: string;
  sku?: string;
  vendorPoEmail?: string;
  poItems?: PoItem[];
  shippingWeightLb?: number;
  lengthIn?: number;
  widthIn?: number;
  heightIn?: number;
  active: boolean;
}

interface StripePrice {
  id: string;
  unitAmount: number | null;
  currency: string;
  nickname: string | null;
  recurring: { interval: string; usageType: string } | null;
}
interface StripeProduct { id: string; name: string; prices: StripePrice[] }

type Draft = Omit<StoreProduct, 'productId' | 'active'> & { productId?: string; active: boolean };

const BLANK: Draft = {
  name: '', description: '', stripePriceId: '', stripeProductId: '', priceCents: 0,
  imageUrl: '', imageKey: '', category: '', sku: '', vendorPoEmail: '', poItems: [], active: true,
};

const dollars = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const priceLabel = (p: StripePrice) =>
  `${p.unitAmount != null ? dollars(p.unitAmount) : '—'} ${p.currency.toUpperCase()}` +
  `${p.recurring ? ` / ${p.recurring.interval}${p.recurring.usageType === 'metered' ? ' (metered)' : ''}` : ''}` +
  `${p.nickname ? ` · ${p.nickname}` : ''}`;

export function StoreProducts() {
  const isAdmin = useIsSiteAdmin();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['admin-products'],
    queryFn: () => api.get<StoreProduct[]>('/admin/products'),
    enabled: isAdmin,
  });
  const stripeProducts = useQuery({
    queryKey: ['admin-stripe-products'],
    queryFn: () => api.get<StripeProduct[]>('/admin/stripe/products'),
    enabled: isAdmin,
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  if (!isAdmin) {
    return <div className="panel empty"><h3>Restricted</h3><p>Store product management is available to Lakota platform staff only.</p></div>;
  }

  const rows = list.data ?? [];
  const editing: Draft | undefined = creating
    ? { ...BLANK }
    : (() => {
        const p = rows.find((r) => r.productId === selected);
        return p ? { ...p } : undefined;
      })();

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Store products</h1>
          <div className="sub">Add, edit and retire store products. Pick the Stripe product &amp; price; the price is set automatically.</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setCreating(true); setSelected(null); }}>+ New product</button>
      </div>

      <div className="admin-split">
        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          {list.isLoading ? <p className="loading-screen">// loading…</p> : rows.length === 0 ? (
            <div className="panel empty" style={{ boxShadow: 'none' }}><h3>No products</h3><p>Add your first store product.</p></div>
          ) : (
            <table className="tx-table">
              <thead><tr><th></th><th>Product</th><th>Price</th><th>Status</th></tr></thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.productId} className={selected === p.productId && !creating ? 'row-selected' : 'row-click'}
                    onClick={() => { setSelected(p.productId); setCreating(false); }}>
                    <td style={{ width: 44 }}>
                      {p.imageUrl
                        ? <img src={p.imageUrl} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6 }} />
                        : <div style={{ width: 36, height: 36, borderRadius: 6, background: 'var(--surface-2)' }} />}
                    </td>
                    <td>{p.name}<div className="acct-code">{p.category || '—'}{p.sku ? ` · ${p.sku}` : ''}</div></td>
                    <td className="mono">{dollars(p.priceCents)}</td>
                    <td><span className={`status-dot ${p.active ? 'ok' : 'muted'}`}>{p.active ? 'Active' : 'Hidden'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div>
          {editing ? (
            <ProductEditor
              key={creating ? '__new__' : selected ?? ''}
              initial={editing}
              creating={creating}
              stripeProducts={stripeProducts.data ?? []}
              stripeLoading={stripeProducts.isLoading}
              stripeError={stripeProducts.isError}
              onSaved={(p) => {
                qc.invalidateQueries({ queryKey: ['admin-products'] });
                qc.invalidateQueries({ queryKey: ['store-products'] });
                setCreating(false);
                setSelected(p.productId);
              }}
              onDeleted={() => {
                qc.invalidateQueries({ queryKey: ['admin-products'] });
                qc.invalidateQueries({ queryKey: ['store-products'] });
                setSelected(null);
                setCreating(false);
              }}
            />
          ) : (
            <div className="panel empty"><h3>Select a product</h3><p>Choose a product to edit, or add a new one.</p></div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProductEditor({
  initial, creating, stripeProducts, stripeLoading, stripeError, onSaved, onDeleted,
}: {
  initial: Draft;
  creating: boolean;
  stripeProducts: StripeProduct[];
  stripeLoading: boolean;
  stripeError: boolean;
  onSaved: (p: StoreProduct) => void;
  onDeleted: () => void;
}) {
  const [form, setForm] = useState<Draft>(initial);
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(initial.imageUrl || null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  useEffect(() => { setForm(initial); setErr(null); setPreview(initial.imageUrl || null); }, [initial]);

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setForm((f) => ({ ...f, [k]: v }));

  // Vendor PO line items (SKU / description / purchase cost) — repeatable rows.
  const poItems = form.poItems ?? [];
  const setPo = (items: PoItem[]) => set('poItems', items);
  const updatePo = (i: number, patch: Partial<PoItem>) => setPo(poItems.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addPo = () => setPo([...poItems, { sku: '', description: '', costCents: 0 }]);
  const removePo = (i: number) => setPo(poItems.filter((_, idx) => idx !== i));

  const selectedProduct = stripeProducts.find((p) => p.id === form.stripeProductId);

  function onPickProduct(productId: string) {
    const prod = stripeProducts.find((p) => p.id === productId);
    setForm((f) => ({
      ...f,
      stripeProductId: productId,
      name: f.name || prod?.name || '',
      // auto-select the only price, else clear until chosen
      ...(prod && prod.prices.length === 1
        ? { stripePriceId: prod.prices[0]!.id, priceCents: prod.prices[0]!.unitAmount ?? 0 }
        : { stripePriceId: '', priceCents: 0 }),
    }));
  }
  function onPickPrice(priceId: string) {
    const price = selectedProduct?.prices.find((p) => p.id === priceId);
    setForm((f) => ({ ...f, stripePriceId: priceId, priceCents: price?.unitAmount ?? 0 }));
  }

  async function onFile(file: File) {
    setUploading(true); setErr(null);
    try {
      const ext = (file.name.split('.').pop() || 'png').toLowerCase();
      const { url, key } = await api.post<{ url: string; key: string }>(
        '/admin/products/image-presign', { contentType: file.type, ext });
      const put = await fetch(url, { method: 'PUT', headers: { 'content-type': file.type }, body: file });
      if (!put.ok) throw new Error(`Upload failed (${put.status})`);
      setForm((f) => ({ ...f, imageKey: key, imageUrl: '' }));
      setPreview(URL.createObjectURL(file));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Image upload failed');
    } finally {
      setUploading(false);
    }
  }

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: form.name.trim(),
        stripePriceId: form.stripePriceId.trim(),
        priceCents: Math.round(Number(form.priceCents) || 0),
        description: form.description?.trim() || undefined,
        stripeProductId: form.stripeProductId?.trim() || undefined,
        imageUrl: form.imageUrl?.trim() || undefined,
        imageKey: form.imageKey?.trim() || undefined,
        category: form.category?.trim() || undefined,
        sku: form.sku?.trim() || undefined,
        vendorPoEmail: form.vendorPoEmail?.trim() || undefined,
        poItems: (form.poItems ?? [])
          .map((r) => ({ sku: r.sku.trim(), description: r.description.trim(), costCents: Math.round(Number(r.costCents) || 0) }))
          .filter((r) => r.sku || r.description),
        shippingWeightLb: Number(form.shippingWeightLb) > 0 ? Number(form.shippingWeightLb) : undefined,
        lengthIn: Number(form.lengthIn) > 0 ? Number(form.lengthIn) : undefined,
        widthIn: Number(form.widthIn) > 0 ? Number(form.widthIn) : undefined,
        heightIn: Number(form.heightIn) > 0 ? Number(form.heightIn) : undefined,
        active: form.active,
      };
      return creating
        ? api.post<StoreProduct>('/admin/products', payload)
        : api.patch<StoreProduct>(`/admin/products/${form.productId}`, payload);
    },
    onSuccess: (p) => { setErr(null); onSaved(p); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Save failed'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/admin/products/${form.productId}`),
    onSuccess: () => onDeleted(),
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Delete failed'),
  });

  const canSave = form.name.trim() && form.stripePriceId.trim() && Number(form.priceCents) > 0;

  return (
    <div className="panel">
      <div className="panel-head"><h3 style={{ margin: 0 }}>{creating ? 'New product' : 'Edit product'}</h3></div>

      <div className="demo-grid" style={{ marginTop: 16 }}>
        <label className="field"><span className="field-label">Stripe product</span>
          {stripeError ? (
            <span className="field-help" style={{ color: 'var(--danger)' }}>Couldn't load Stripe products.</span>
          ) : (
            <select className="select" value={form.stripeProductId ?? ''} onChange={(e) => onPickProduct(e.target.value)}>
              <option value="">{stripeLoading ? 'Loading…' : 'Select a product…'}</option>
              {stripeProducts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              {/* keep an unknown stored product id selectable */}
              {form.stripeProductId && !stripeProducts.some((p) => p.id === form.stripeProductId) && (
                <option value={form.stripeProductId}>{form.stripeProductId} (current)</option>
              )}
            </select>
          )}
        </label>

        <label className="field"><span className="field-label">Stripe price</span>
          <select className="select" value={form.stripePriceId ?? ''} onChange={(e) => onPickPrice(e.target.value)}
            disabled={!selectedProduct}>
            <option value="">{selectedProduct ? 'Select a price…' : 'Pick a product first'}</option>
            {selectedProduct?.prices.map((pr) => <option key={pr.id} value={pr.id}>{priceLabel(pr)}</option>)}
            {form.stripePriceId && !selectedProduct?.prices.some((pr) => pr.id === form.stripePriceId) && (
              <option value={form.stripePriceId}>{form.stripePriceId} (current)</option>
            )}
          </select>
        </label>

        <label className="field"><span className="field-label">Price</span>
          <input className="input mono" value={form.priceCents ? dollars(form.priceCents) : '—'} readOnly
            style={{ background: 'var(--surface-2)' }} title="Set automatically from the selected Stripe price" />
        </label>

        <label className="field" style={{ gridColumn: '1 / -1' }}><span className="field-label">Name</span>
          <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. Suprema RealScan-G10" /></label>

        <label className="field" style={{ gridColumn: '1 / -1' }}><span className="field-label">Description</span>
          <textarea className="input" rows={2} value={form.description ?? ''} onChange={(e) => set('description', e.target.value)} /></label>

        <label className="field"><span className="field-label">Category</span>
          <input className="input" value={form.category ?? ''} onChange={(e) => set('category', e.target.value)} placeholder="e.g. scanner, supplies" /></label>

        <label className="field"><span className="field-label">SKU</span>
          <input className="input" value={form.sku ?? ''} onChange={(e) => set('sku', e.target.value)} /></label>

        <label className="field"><span className="field-label">Vendor PO email</span>
          <input className="input" type="email" value={form.vendorPoEmail ?? ''} onChange={(e) => set('vendorPoEmail', e.target.value)}
            placeholder="Drop-ship recipient (optional)" /></label>

        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Purchase Order line items</span>
          <span className="field-help" style={{ marginBottom: 8 }}>
            SKU, description and purchase (wholesale) cost sent to the vendor PO email when this product is ordered.
            These never appear on the customer's order confirmation.
          </span>
          {poItems.length > 0 && (
            <div className="po-items">
              <div className="po-row po-row-head">
                <span>SKU</span><span>Description</span><span>Purchase cost</span><span></span>
              </div>
              {poItems.map((r, i) => (
                <div className="po-row" key={i}>
                  <input className="input" value={r.sku} onChange={(e) => updatePo(i, { sku: e.target.value })} placeholder="SKU-123" />
                  <input className="input" value={r.description} onChange={(e) => updatePo(i, { description: e.target.value })} placeholder="Item description" />
                  <input className="input mono" inputMode="decimal" placeholder="0.00"
                    value={r.costCents ? (r.costCents / 100).toFixed(2) : ''}
                    onChange={(e) => updatePo(i, { costCents: Math.round((parseFloat(e.target.value) || 0) * 100) })} />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => removePo(i)} title="Remove">✕</button>
                </div>
              ))}
            </div>
          )}
          <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: 8, alignSelf: 'flex-start' }} onClick={addPo}>
            + Add PO item
          </button>
        </div>

        {/* Shipping (FedEx rating) */}
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Shipping</span>
          <span className="field-help" style={{ marginBottom: 8 }}>
            Weight is required for physical goods — it drives the FedEx rate at checkout. Dimensions are
            optional (used for dimensional weight; a default box is assumed when blank).
          </span>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <label className="field" style={{ maxWidth: 150 }}><span className="field-label">Weight (lb)</span>
              <input className="input" type="number" min="0" step="0.1" value={form.shippingWeightLb ?? ''}
                onChange={(e) => set('shippingWeightLb', e.target.value === '' ? undefined : Number(e.target.value))} placeholder="2.5" /></label>
            <label className="field" style={{ maxWidth: 120 }}><span className="field-label">Length (in)</span>
              <input className="input" type="number" min="0" step="0.1" value={form.lengthIn ?? ''}
                onChange={(e) => set('lengthIn', e.target.value === '' ? undefined : Number(e.target.value))} /></label>
            <label className="field" style={{ maxWidth: 120 }}><span className="field-label">Width (in)</span>
              <input className="input" type="number" min="0" step="0.1" value={form.widthIn ?? ''}
                onChange={(e) => set('widthIn', e.target.value === '' ? undefined : Number(e.target.value))} /></label>
            <label className="field" style={{ maxWidth: 120 }}><span className="field-label">Height (in)</span>
              <input className="input" type="number" min="0" step="0.1" value={form.heightIn ?? ''}
                onChange={(e) => set('heightIn', e.target.value === '' ? undefined : Number(e.target.value))} /></label>
          </div>
        </div>

        {/* Product image */}
        <div className="field" style={{ gridColumn: '1 / -1' }}>
          <span className="field-label">Product image</span>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            {preview
              ? <img src={preview} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
              : <div style={{ width: 72, height: 72, borderRadius: 8, background: 'var(--surface-2)', border: '1px solid var(--border)' }} />}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
              {uploading ? 'Uploading…' : preview ? 'Replace image' : 'Upload image'}
            </button>
            {(form.imageKey || form.imageUrl) && (
              <button type="button" className="btn btn-ghost btn-sm"
                onClick={() => { setForm((f) => ({ ...f, imageKey: '', imageUrl: '' })); setPreview(null); }}>Remove</button>
            )}
          </div>
          <span className="field-help" style={{ marginTop: 6 }}>PNG/JPEG/WebP/GIF. Or paste an external URL below.</span>
          <input className="input" style={{ marginTop: 6 }} value={form.imageUrl ?? ''}
            onChange={(e) => { set('imageUrl', e.target.value); set('imageKey', ''); setPreview(e.target.value || null); }}
            placeholder="https://… (optional external image)" />
        </div>

        <label className="checkbox" style={{ gridColumn: '1 / -1' }}>
          <input type="checkbox" checked={form.active} onChange={(e) => set('active', e.target.checked)} />
          <span>Active (shown in the store)</span>
        </label>
      </div>

      {err && <p className="cart-error" style={{ marginTop: 14 }}>{err}</p>}

      <div style={{ display: 'flex', gap: 10, marginTop: 18, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
          {save.isPending ? 'Saving…' : creating ? 'Create product' : 'Save changes'}
        </button>
        {!creating && (
          <button
            className="btn btn-ghost"
            onClick={() => { if (window.confirm(`Delete "${form.name}"? This cannot be undone.`)) remove.mutate(); }}
            disabled={remove.isPending}
          >
            {remove.isPending ? 'Deleting…' : 'Delete'}
          </button>
        )}
      </div>
    </div>
  );
}
